import { useState } from "react";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { UiTranslations } from "@/lib/ui-translations";

export interface FeedbackData {
  rating?: number;
  tags?: string[];
  comment?: string;
}

interface FeedbackScreenProps {
  onSubmit: (feedback: FeedbackData) => void;
  uiT?: UiTranslations;
}

export function FeedbackScreen({ onSubmit, uiT }: FeedbackScreenProps) {
  const [rating, setRating] = useState<number | undefined>();
  const [comment, setComment] = useState("");
  const [hoveredStar, setHoveredStar] = useState<number | undefined>();
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (submitted) return;
    setSubmitted(true);
    onSubmit({
      rating,
      tags: selectedTags.size > 0 ? Array.from(selectedTags) : undefined,
      comment: comment.trim() || undefined,
    });
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const tags = [
    { id: "too_many_questions", label: uiT?.feedbackTagTooMany ?? "Trop de questions" },
    { id: "confusing", label: uiT?.feedbackTagConfusing ?? "Questions pas claires" },
    { id: "bugs", label: uiT?.feedbackTagBugs ?? "Bugs ou erreurs" },
    { id: "slow", label: uiT?.feedbackTagSlow ?? "Trop lent" },
    { id: "documents", label: uiT?.feedbackTagDocuments ?? "Problèmes avec les documents" },
  ];

  const showTags = rating !== undefined && rating <= 3;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-card rounded-2xl shadow-card p-6 md:p-8 space-y-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">
              {uiT?.feedbackTitle ?? "Votre avis nous aide"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {uiT?.feedbackSubtitle ?? "Deux questions rapides sur votre expérience — entièrement optionnelles."}
            </p>
          </div>

          {/* Star rating */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">
              {uiT?.feedbackRatingLabel ?? "Comment évalueriez-vous votre expérience globale ?"}
            </p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hoveredStar ?? rating ?? 0) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      setRating(n);
                      if (n > 3) setSelectedTags(new Set());
                    }}
                    onMouseEnter={() => setHoveredStar(n)}
                    onMouseLeave={() => setHoveredStar(undefined)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={cn(
                        "w-8 h-8 transition-colors",
                        filled
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-muted-foreground/40",
                      )}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tags (only for low ratings) */}
          {showTags && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {uiT?.feedbackTagsLabel ?? "Qu'est-ce qui pourrait être amélioré ?"}
              </p>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                      selectedTags.has(tag.id)
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:border-primary/40"
                    )}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comment */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              {uiT?.feedbackCommentLabel ?? "Des commentaires ou suggestions ?"}{" "}
              <span className="text-muted-foreground font-normal">
                ({uiT?.feedbackOptional ?? "optionnel"})
              </span>
            </p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={uiT?.feedbackCommentPlaceholder ?? "Écrivez vos commentaires ici..."}
              className="resize-none min-h-[80px]"
            />
          </div>

          {/* Actions */}
          <div className="pt-2">
            <Button onClick={handleSubmit} disabled={submitted} className="w-full h-12 text-base font-medium gap-2">
              {uiT?.feedbackContinue ?? "Continuer vers la révision"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="text-center mt-4 pb-4">
          <p className="text-xs text-muted-foreground/60">
            Powered by <span className="font-medium text-primary">Formio</span>
          </p>
        </div>
      </div>
    </div>
  );
}
